import { Contact } from '@eigtech/contacts-types'
import { Attendee, Meeting, MeetingDetails, Participant } from '@eigtech/meetings-types'
import {
  MeetingManager,
  useMeetingManager,
  useMeetingStatus,
} from 'amazon-chime-sdk-component-library-react'
import { pick } from 'lodash-es'
import { useEffect } from 'react'
import { useMount } from 'react-use'
import { useShallow } from 'zustand/shallow'
import { useStartMeetingManager } from '../api/startMeetingManager'
import { MeetingState, useMeetingStore } from '../context/MeetingState'
import { useGetAttendee } from './useGetAttendee'

export type UseChimeMeetingProps = {
  canScreenshot?: boolean
  chimeAttendee: Attendee
  chimeMeeting: Meeting
  getAttendee?: MeetingManager['getAttendee']
  invitableContacts?: Contact[]
  meetingDetails: MeetingDetails
  participant: Participant
  onLeaveMeeting?: MeetingState['onLeaveMeeting']
}

export function useChimeMeeting({
  getAttendee: propsGetAttendee,
  ...meetingInfo
}: UseChimeMeetingProps) {
  const { chimeAttendee, chimeMeeting, meetingDetails, setMeetingInformation } = useMeetingStore(
    useShallow(stateSelector)
  )
  const { mutateAsync: startMeetingManager, isPending, isError, error } = useStartMeetingManager()

  const meetingManager = useMeetingManager()
  const meetingStatus = useMeetingStatus()

  const getAttendee = useGetAttendee({
    entityId: meetingInfo.meetingDetails.entityId,
    meetingId: meetingInfo.meetingDetails.meetingId,
    startTime: meetingInfo.meetingDetails.startTime,
  })

  useEffect(() => {
    meetingManager.getAttendee = propsGetAttendee ?? getAttendee
  }, [meetingManager, getAttendee, propsGetAttendee])

  useMount(() => {
    if (meetingDetails && chimeAttendee && chimeMeeting) {
      return
    }

    setMeetingInformation(meetingInfo)
    startMeetingManager(meetingInfo)
  })

  return { meetingDetails, meetingStatus, isPending, isError, error }
}

const stateSelector = (state: MeetingState) =>
  pick(state, ['chimeAttendee', 'chimeMeeting', 'setMeetingInformation', 'meetingDetails'])
